import { useEffect, useState } from "react";
import { X, MessageSquare, Clock, MapPin, CalendarX2 } from "lucide-react";
import { formatDate } from "./bookingUtils.jsx";

export default function AddRemarksModal({ booking, onClose, onSave, onCancelBooking }) {
    const [remarks, setRemarks] = useState("");
    const [confirmingCancel, setConfirmingCancel] = useState(false);

    // Reset the textarea whenever a new booking is opened, and pre-fill
    // with any remarks already saved for it.
    useEffect(() => {
        if (booking) {
            setRemarks(booking.remarks || "");
            setConfirmingCancel(false);
        }
    }, [booking]);

    if (!booking) return null;

    const handleSave = () => {
        onSave(booking.id, remarks.trim());
        onClose();
    };

    const handleCancelMeeting = () => {
        onCancelBooking?.(booking.id);
        onClose();
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4">
            <div className="w-full max-w-md rounded-2xl bg-white shadow-xl border border-slate-200">
                {/* Header */}
                <div className="flex items-start justify-between px-5 py-4 border-b border-slate-100">
                    <div className="flex items-center gap-3">
                        <div className="h-9 w-9 shrink-0 rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center text-xs font-semibold text-white">
                            {booking.initials}
                        </div>
                        <div>
                            <p className="font-medium text-slate-900 text-sm">{booking.mentorName}</p>
                            <p className="text-xs text-slate-400">{booking.course}</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-slate-400 hover:text-slate-600 transition-colors"
                    >
                        <X className="h-4.5 w-4.5" />
                    </button>
                </div>

                {/* Meeting details */}
                <div className="px-5 py-4 space-y-2 border-b border-slate-100 bg-slate-50/60">
                    <p className="flex items-center gap-1.5 text-xs text-slate-500">
                        <Clock className="h-3.5 w-3.5 text-slate-400" />
                        {formatDate(booking.date)} · {booking.time}
                    </p>
                    <p className="flex items-center gap-1.5 text-xs text-slate-500">
                        <MapPin className="h-3.5 w-3.5 text-slate-400" />
                        {booking.location}
                    </p>
                </div>

                {/* Remarks form */}
                <div className="px-5 py-4">
                    <label className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-slate-500 mb-2">
                        <MessageSquare className="h-3.5 w-3.5" />
                        Remarks
                    </label>
                    <textarea
                        autoFocus
                        rows={4}
                        value={remarks}
                        onChange={(e) => setRemarks(e.target.value)}
                        placeholder="Add notes about this meeting…"
                        className="w-full resize-none rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none focus:border-indigo-300 focus:ring-2 focus:ring-indigo-100 transition"
                    />
                </div>

                {/* Cancel meeting section */}
                <div className="px-5 pb-4">
                    {!confirmingCancel ? (
                        <button
                            onClick={() => setConfirmingCancel(true)}
                            className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-100 transition-colors"
                        >
                            <CalendarX2 className="h-3.5 w-3.5" />
                            Cancel meeting
                        </button>
                    ) : (
                        <div className="rounded-xl border border-red-200 bg-red-50 px-3.5 py-3">
                            <p className="text-xs text-red-700 mb-2.5">
                                Are you sure you want to cancel this meeting? This can't be undone.
                            </p>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={handleCancelMeeting}
                                    className="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700 transition-colors"
                                >
                                    Yes, cancel it
                                </button>
                                <button
                                    onClick={() => setConfirmingCancel(false)}
                                    className="rounded-lg px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-100 transition-colors"
                                >
                                    Keep meeting
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Actions */}
                <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-100">
                    <button
                        onClick={onClose}
                        className="rounded-lg px-3.5 py-2 text-xs font-medium text-slate-600 hover:bg-slate-100 transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        className="rounded-lg bg-indigo-600 px-3.5 py-2 text-xs font-medium text-white hover:bg-indigo-700 transition-colors"
                    >
                        Save remarks
                    </button>
                </div>
            </div>
        </div>
    );
}